<template>
  <HeadTitle
    title="Crear grupo de nóminas"
    description="Formulario de creación de un grupo de nominas que contiene parámetros de configuración"
  />
  <br />
  <Step :stepNumber="1" title="Seleccionar compañia" :required="true">
    <AutoComplete
      v-model="selectedCompany"
      :optionLabel="companyLabel"
      :suggestions="filteredCompanies"
      @complete="search"
      @item-select="mountPayrolls"
      :loading="isLoadingCompanies"
    >
      <template #option="slotProps">
        <div class="flex align-options-center">
          <div>{{ slotProps.option.code + ' - ' + slotProps.option.name }}</div>
        </div>
      </template>
    </AutoComplete>
  </Step>
  <Divider />
  <Step :stepNumber="2" title="Seleccionar nóminas" :required="true">
    <DataTable
      v-model:filters="filters"
      class="lg:w-8 md:w-12"
      scrollable
      scrollHeight="400px"
      v-model:selection="selectedPayrolls"
      :value="payrolls"
      dataKey="payrollId"
      tableStyle="min-width: 50rem"
      :globalFilterFields="['code', 'name', 'payrollId', 'isInactive']"
      :loading="isLoadingPayrolls"
    >
      <template #header>
        <div class="flex justify-content-end">
          <span class="p-input-icon-left">
            <i class="pi pi-search" />
            <InputText v-model="filters['global'].value" placeholder="Buscar" />
          </span>
        </div>
      </template>

      <template #empty> No se encontraron nóminas. </template>
      <Column selectionMode="multiple" headerStyle="width: 3rem"></Column>
      <Column field="code" header="Código" sortable></Column>
      <Column field="name" header="Nombre"></Column>
      <Column header="Plantilla de recibo" sortable>
        <template #body="slotProps">
          <Dropdown
            v-model="slotProps.data.receiptTemplateId"
            :options="receiptTemplates"
            optionLabel="name"
            optionValue="id"
            checkmark
            placeholder="default"
          />
        </template>
      </Column>
    </DataTable>
  </Step>
  <Divider />
  <Step :step-number="3" title="Configuración">
    <p class="font-light text-sm my-3 w-12 pb-4">
      Configura las opciones disponibles para este grupo de nóminas, como los
      módulos habilitados, la descripción, los días de pago y otras
      preferencias.
    </p>
    <div class="grid px-2 md:w-6 sm:w-12">
      <div class="flex flex-column md:col-6 sm:col-12">
        <label for="modules" class="text-md"
          >Módulos disponibles<span class="text-red-600 ml-2">*</span></label
        >
        <MultiSelect
          v-model="selectedModules"
          :options="modules"
          filter
          optionLabel="name"
          placeholder="Seleccionar módulos"
          class="w-auto mt-2 mr-5"
          :max-selected-labels="3"
          :disabled="!selectedPayrolls"
        />
        <small id="text-error" class="p-error">{{
          moduleError || '&nbsp;'
        }}</small>
      </div>
      <div class="flex flex-column h-5 md:col-6 sm:col-12">
        <label for="description"
          >Descripción <span class="text-red-600">*</span></label
        >
        <Textarea
          class="mt-2"
          v-model="description"
          :disabled="!selectedPayrolls"
        />
        <small id="text-error" class="p-error">{{
          descriptionError || '&nbsp;'
        }}</small>
      </div>
      <div class="flex flex-column md:col-6 sm:col-12 pr-5">
        <label for="paydays">Días de pago</label>
        <InputText
          id="paydays"
          class="mt-2"
          v-model="paydays"
          :disabled="!selectedPayrolls"
          placeholder="ej. 15,30"
        />
        <small id="text-error" class="p-error">{{
          paydaysError || '&nbsp;'
        }}</small>
      </div>
      <div class="flex flex-column">
        <div class="flex flex-row text-md align-items-center mb-3">
          <InputSwitch
            class="mr-2"
            v-model="automatedEmployeeCreation"
            :disabled="!selectedPayrolls"
          />
          <label for="employeesAutomated">Actualizar empleados</label>
          <InfoButton
            message="Al activar esta opción se actualizarán los datos de los empleados de este grupo de nóminas cada hora del día."
          />
        </div>
        <div class="flex flex-row align-items-center mb-3">
          <InputSwitch
            class="mr-2"
            v-model="seeReceiptUntilEndDate"
            :disabled="!selectedPayrolls"
          />
          <label for="seeReceiptsUntilEndDate" class="text-md"
            >Mostrar recibos hasta el fin del periodo</label
          >
          <InfoButton
            message="Ejemplo: fecha inicial: 15-09-2024 - fecha final : 30-09-2024, si existe un comprobante de pago dentro de este periodo, este se muestra a los empleado el mismo dia o posterior a la fecha final del periodo"
          />
        </div>
        <div class="flex flex-row align-items-center mb-3">
          <InputSwitch
            v-model="allowPayedVacations"
            :disabled="!selectedPayrolls"
            class="mr-2"
          />
          <label for="allow-paid-vacations"
            >Permitir vacaciones en dinero</label
          >
          <InfoButton
            message="Al activar esta opción, los empleados podrán solicitar el pago de sus vacaciones en lugar de tomarlas como tiempo libre."
          />
        </div>
        <div class="flex flex-row align-items-center mb-3">
          <InputSwitch
            v-model="allowNegativePTOBalance"
            :disabled="!selectedPayrolls"
            class="mr-2"
          />
          <label for="allow-paid-vacations"
            >Permitir saldo negativo de vacaciones</label
          >
          <InfoButton
            message="Al activar esta opción, los empleados podrán tener un saldo negativo en su balance de vacaciones y pedir vacaciones incluso si no tienen saldo disponible."
          />
        </div>
      </div>
    </div>
    <template v-if="showNotificationSection">
      <Divider />
      <h5 class="w-12 text-700">Envio de reportes de solicitudes aprobadas</h5>
      <br />
      <div class="flex flex-column md:w-4 ml-2 mt-4">
        <label for="emailsToNotify">Direccion de correo electronico</label>
        <Textarea
          id="emailsToNotify"
          class="mt-2"
          v-model="emailsToNotify"
          :disabled="!selectedPayrolls"
        />
        <small id="text-error" class="p-error">{{
          emailsError || '&nbsp;'
        }}</small>
      </div>

      <div class="flex flex-column md:w-1 mt-4 ml-3">
        <label for="notificationDate">Día del mes</label>
        <InputNumber
          id="notificationDate"
          class="mt-2"
          v-model="notificationDate"
          inputId="minmax-buttons"
          showButtons
          :min="1"
          :max="30"
          :pt="{
            input: {
              class: 'w-1',
            },
          }"
        />
      </div>
    </template>
  </Step>
  <Footer>
    <Button
      label="Cancelar"
      severity="secondary"
      @click="router.back()"
      class="mr-2 font-light text-sm"
    />
    <Button
      label="Guardar"
      :disabled="!isValidToSave"
      severity="primary"
      @click="save"
      class="mr-2 font-light text-sm"
      :loading="saving"
    />
  </Footer>
</template>

<script lang="ts" setup>
  import { computed, onMounted, ref, watch } from 'vue'
  import { FilterMatchMode } from 'primevue/api'
  import * as yup from 'yup'

  //COMPONENTS
  import Step from '@/shared/components/Step.vue'
  import Footer from '@/shared/components/Footer.vue'

  // SERVICES
  import {
    getPayrollsIntegrationByCompanyId,
    createPayrollConfig,
  } from '../services/payrollsConfigService'
  import { getModules } from '../../../shared/services/ModuleService'

  import { useField } from 'vee-validate'
  import { Module } from '../models'
  import router from '@/router'
  import useNotifications from '@/store/notification'
  import Company from '@/modules/companies/models/Company'
  import useCompanies from '@/shared/composables/useCompanies'
  import useCompany from '@/store/company'
  import { PayrollConfigInput } from '../services/interfaces'
  import { getReceiptTemplates } from '../services/ReceiptTemplateService'
  import InfoButton from '@/shared/components/InfoButton.vue'
  import HeadTitle from '@/shared/components/HeadTitle.vue'

  const filteredCompanies = ref()
  const companies = ref()
  const payrolls = ref()
  const selectedPayrolls = ref()
  const modules = ref()
  const automatedEmployeeCreation = ref(true)
  const seeReceiptUntilEndDate = ref(true)
  const showNotificationSection = ref(false)
  const receiptTemplates = ref()
  const receiptTemplateIdDefault = '63321c62-b4e7-4e21-ac35-b19207f65991' //default receipt template id

  const company = ref()
  const notificationStore = useNotifications()
  const companyStore = useCompany()
  const allowPayedVacations = ref(false)
  const allowNegativePTOBalance = ref(false)

  const descriptionSchema = yup
    .string()
    .required('La descripción del grupo es requerida')
  const modulesSchema = yup
    .array()
    .min(1, 'Al menos un módulo debe ser seleccionado')
    .required('los modulos son requeridos')
  const companySchema = yup.object().required('La compañia es requerida')

  const notificationDateSchema = yup
    .number()
    .required('El dia de notificacion es requerido')
    .min(1, 'El dia de notificacion debe ser mayor a 0')
    .max(30, 'El dia de notificacion debe ser menor a 31')
  //emails are an string separated by commas, please validate this
  const emailsSchema = yup
    .string()
    .test('emails', 'Correo invalido', (value) => {
      if (value) {
        const emails = value.split(',')
        const regex = new RegExp(
          '^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$'
        )
        return emails.every((email) => regex.test(email.trim()))
      }
      return true
    })

  const paydaysSchema = yup
    .string()
    .test('paydays', 'dias de pago invalido', (value) => {
      if (value) {
        const paydays = value.split(',')
        const areValidNumbers = paydays.every(
          (day) =>
            /^\d+$/.test(day.trim()) &&
            parseInt(day.trim(), 10) >= 1 &&
            parseInt(day.trim(), 10) <= 31
        )

        const isValidFormat = /^\d+(,\d+)*$/.test(value)

        return areValidNumbers && isValidFormat
      }
      return true
    })

  const isLoadingCompanies = ref(false)
  const isLoadingPayrolls = ref(false)
  const saving = ref(false)
  // const loadingStore = useLoading()
  //forms validation
  const {
    value: description,
    errorMessage: descriptionError,
    validate: descValidate,
  } = useField<string>('description', descriptionSchema)
  const {
    value: selectedModules,
    errorMessage: moduleError,
    validate: modValidate,
  } = useField<Module[]>('selectedModules', modulesSchema)
  const {
    value: selectedCompany,
    errorMessage: companyError,
    validate: companyValidate,
  } = useField<Company>('selectedCompany', companySchema)
  const {
    value: emailsToNotify,
    errorMessage: emailsError,
    validate: emailsValidate,
  } = useField<string>('emailsToNotify', emailsSchema)
  const {
    value: notificationDate,
    errorMessage: notificationDateError,
    validate: notificationDateValidate,
  } = useField<number>('notificationDate', notificationDateSchema)
  const {
    value: paydays,
    errorMessage: paydaysError,
    validate: paydaysValidate,
  } = useField<string>('paydays', paydaysSchema)

  companyStore.$subscribe(async (_, state) => {
    company.value = state.company
  })

  onMounted(() => {
    mountCompanies()
    getModules({
      onlyAdminModules: false,
    }).then((response) => (modules.value = response.data))
    getReceiptTemplates().then(
      (response) => (receiptTemplates.value = response)
    )
  })

  watch(selectedCompany, () => {
    if (
      selectedCompany.value.client &&
      selectedCompany.value.client.integrationType &&
      selectedCompany
    ) {
      const integrationCode = selectedCompany.value?.client.code
      if (integrationCode === '4') {
        showNotificationSection.value = true
      } else {
        showNotificationSection.value = false
      }
    }
  })

  const mountCompanies = async () => {
    isLoadingCompanies.value = true
    const { companies: Companies } = await useCompanies()
    companies.value = Companies.value
    isLoadingCompanies.value = false
  }

  const mountPayrolls = async () => {
    const id = selectedCompany.value?.id || ''
    isLoadingPayrolls.value = true
    const response = await getPayrollsIntegrationByCompanyId(id)
    payrolls.value = response.data.map((payroll: any) => {
      return {
        ...payroll,
        receiptTemplateId: receiptTemplateIdDefault,
      }
    })

    isLoadingPayrolls.value = false
  }

  const search = (event: any) => {
    setTimeout(() => {
      if (!event.query.trim().length) {
        filteredCompanies.value = [...companies.value]
      } else {
        filteredCompanies.value = companies.value.filter((company: Company) => {
          return (
            company.code
              ?.toLocaleLowerCase()
              .startsWith(event.query.toLocaleLowerCase()) ||
            company.name.toLowerCase().startsWith(event.query.toLowerCase())
          )
        })
      }
    }, 250)
  }

  const companyLabel = () => {
    return selectedCompany.value?.code + ' - ' + selectedCompany.value?.name
  }

  const filters = ref({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  })

  const save = async () => {
    descValidate()
    modValidate()
    saving.value = true

    let emails: string[] = []
    let paydaysArray: string[] = []

    notificationDate.value

    if (emailsToNotify.value) {
      const emailsWithoutLineBreaks = emailsToNotify.value.replace(
        /(\r\n|\n|\r)/gm,
        ''
      )

      emails = emailsWithoutLineBreaks.includes(',')
        ? emailsWithoutLineBreaks.split(',')
        : emailsWithoutLineBreaks.trim().length > 0
        ? [emailsWithoutLineBreaks]
        : []
    }

    if (paydays.value) {
      const paydaysWithoutSpaces = paydays.value.replace(/(\r\n|\n|\r)/gm, '')

      paydaysArray = paydaysWithoutSpaces.includes(',')
        ? paydaysWithoutSpaces.split(',')
        : paydaysWithoutSpaces.trim().length > 0
        ? [paydaysWithoutSpaces]
        : []
    }

    let payrollConfigInput: PayrollConfigInput = {
      companyId: selectedCompany.value.id,
      description: description.value,
      modules: selectedModules.value.map((m) => m.id),
      payrolls: selectedPayrolls.value,
      automatedEmployeeCreation: automatedEmployeeCreation.value,
      seeReceiptUntilEndDate: seeReceiptUntilEndDate.value,
      allowPayedVacations: allowPayedVacations.value,
      allowNegativePTOBalance: allowNegativePTOBalance.value,
      emailsToNotify: emails,
      paydays: paydaysArray,
    }

    if (notificationDate.value)
      payrollConfigInput.notificationDate = notificationDate.value.toString()

    const response = await createPayrollConfig(payrollConfigInput)
    saving.value = false

    if (response === 'OK') {
      await notificationStore.addSuccesNotification({
        detail: `200`,
        severity: 'success',
        summary: `Grupo de nóminas creado con exito`,
      })

      router.push('/payrollsConfig')
    }
  }

  const isValidToSave = computed(
    () =>
      selectedModules.value?.length > 0 &&
      description.value &&
      selectedPayrolls.value?.length > 0
  )
</script>

<style scoped>
  :deep(.p-inputnumber-buttons-stacked .p-inputnumber-input) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    width: 70px !important;
  }

  :deep(.p-column-title) {
    font-size: 14px;
  }

  :deep(.p-datatable-tbody) {
    font-size: 14px;
  }

  :deep(.p-paginator) {
    display: flex;
    align-items: center;
    justify-content: end;
    flex-wrap: wrap;
    font-size: 14px;
  }
</style>
